import { useEffect, useMemo } from "react";
import { trpc } from "utils/trpc";
import Head from "next/head";
import { PageContextProvider } from "lib/context/page-context";
import { Page } from "components/Page";
import PoweredByZapier from "components/PoweredByZapier";
import { ThemeProvider } from "lib/theme";
import { useProjectTheme } from "lib/hooks/useProjectTheme";
import GATrackingScript from "components/GATrackingScript";
import { ProjectAuthType } from "@prisma/client";
import { useTrackingContext } from "observability/tracking";
import { navigationItemsForPages } from "lib/utils/navigationItemForPages";
import { PublishedPageSpinner } from "blocks/components/PublishedPageSpinner";
import { useCurrentProject } from "lib/context/current-project-context";
import { CurrentPagesProvider } from "lib/context/current-pages-context";
import { DatasourcesProvider } from "lib/context/datasources-provider";
import { CurrentPageProvider } from "lib/context/current-page-context";
import { userHasAccess } from "lib/utils/stripe";

type PublishedPageRendererProps = {
  pageId: string | undefined;
  pageTitle: string;
  isEmbed: boolean;
};

export function PublishedPageRenderer({
  pageId,
  pageTitle,
  isEmbed,
}: PublishedPageRendererProps) {
  const project = useCurrentProject();
  const projectId = project.id;

  const {
    data: page,
    error: pageError,
    isLoading: isPageLoading,
  } = trpc.pages.get.useQuery(
    { id: pageId ?? "" },
    {
      suspense: false,
    }
  );
  const {
    data: maybePages,
    error: pagesError,
    isLoading: isPagesLoading,
  } = trpc.pages.list.useQuery(
    { projectId },
    {
      enabled: !!project,
      suspense: false,
    }
  );
  const pages = useMemo(() => maybePages ?? [], [maybePages]);
  const allPages = useMemo(
    () => navigationItemsForPages(pages, project),
    [pages, project]
  );

  const { data: consumerInfo } = trpc.consumers.me.useQuery(undefined, {
    suspense: false,
    enabled: Boolean(project && project.authType === ProjectAuthType.Consumer),
  });

  const projectTheme = useProjectTheme({
    projectAppearance: project?.appearance,
  });
  const favicon = project?.favicon?.url;

  const { emitConsumerInteractionEvent } = useTrackingContext();

  useEffect(() => {
    if (pageId && projectId) {
      emitConsumerInteractionEvent({
        interfaces_project_id: projectId,
        interfaces_page_id: pageId,
        embedded_flag: isEmbed,
        interaction_goal: "see page",
        event_action: "enter",
      });
    }
  }, [isPageLoading, emitConsumerInteractionEvent, projectId, pageId, isEmbed]);

  const hasChatbot = useMemo(() => {
    return (
      page?.content.blocks.some((b) => b.type === "chatbot-block") ?? false
    );
  }, [page]);

  if (isPagesLoading || isPageLoading) {
    return (
      <PublishedPageSpinner
        ariaLabelSuffix={[
          isPagesLoading ? "All pages" : "",
          isPageLoading ? "Page" : "",
        ]
          .filter(Boolean)
          .join(", ")}
      />
    );
  }

  if (!page || !project || pagesError || pageError)
    throw new Error("Page did not load");

  return (
    <>
      <div style={{ minHeight: "100vh" }}>
        <Head>
          <title>{pageTitle}</title>
          {favicon && (
            <>
              <link
                rel="apple-touch-icon"
                sizes="180x180"
                href={`${favicon}-/resize/180x180/`}
                key="favicon-apple-touch"
              />
              <link
                rel="icon"
                type="image/png"
                sizes="32x32"
                href={`${favicon}-/resize/32x32/`}
                key="favicon-32"
              />
              <link
                rel="icon"
                type="image/png"
                sizes="16x16"
                href={`${favicon}-/resize/16x16/`}
                key="favicon"
              />
            </>
          )}
        </Head>
        <ThemeProvider projectTheme={projectTheme}>
          <PageContextProvider
            isEditing={false}
            accountId={project?.accountId}
            projectId={page.projectId}
            pageId={page.id}
            allPages={allPages}
            globalNavigation={project.globalNavigation}
            includeLogo={project.includeLogo}
            navigationLinks={project.navigationLinks}
            projectName={project.name}
            projectSlug={project.slug}
            navigationEnabled={page.navigationEnabled}
            logo={project.logo}
          >
            <CurrentPageProvider page={page}>
              <CurrentPagesProvider pages={pages}>
                <DatasourcesProvider>
                  <Page
                    blocks={page.content.blocks ?? []}
                    consumerInfo={consumerInfo}
                  />
                </DatasourcesProvider>
              </CurrentPagesProvider>
            </CurrentPageProvider>
            <PoweredByZapier hasChatbot={hasChatbot} position="fixed-portal" />
          </PageContextProvider>
        </ThemeProvider>
      </div>
      {userHasAccess(
        project.creator.planId,
        project.creator.isStaff,
        "analytics"
      ) &&
        project?.googleAnalyticsTrackingId && (
          <GATrackingScript trackingId={project.googleAnalyticsTrackingId} />
        )}
    </>
  );
}
